// Wave AW.3-React (2026-05-16) — 외부 바이어 경매 상세 화면
// Wave UX-2.1 Item 1 sweep (2026-05-23) — 4 locale i18n 마이그레이션.
// API: GET /buyer-app/auction/:id

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API } from '../../utils/api';
import { authBuyerFetch, isUnauthorized, handleUnauthorized, displayApiError } from './buyerApi';
import AuctionBuyerLayout from './AuctionBuyerLayout';
import AuctionBuyerBidModal from './AuctionBuyerBidModal';
import AuthImage from './AuthImage';
import StatsCard from './StatsCard';
import PhotoViewerModal from './PhotoViewerModal';
import ReportModal from './ReportModal';
import MarketPriceCard from '../MarketPriceCard';  // W6 — AUTODEX 시세 (외부 바이어, KRW+USD)
import { useT, useLocale } from '../../i18n/I18nProvider';
import { formatKrwAmount, formatKrwAmountWithConvert, useExchangeRates } from '../../i18n/currency';
import TrustSpeedBadge from '../TrustSpeedBadge';
import { gradeI18nKey } from '../../utils/trustSpeedGrades';

function formatKrw(n, t) { return formatKrwAmount(n, t); }
function formatMileage(km) {
  if (km == null) return '';
  return Number(km).toLocaleString() + ' km';
}
function formatRemaining(endsAt, t) {
  if (!endsAt) return '';
  var end = new Date(endsAt).getTime();
  var diff = end - Date.now();
  if (diff <= 0) return t ? t('detailEnded') : '마감됨';
  var d = Math.floor(diff / 86400000);
  var h = Math.floor((diff % 86400000) / 3600000);
  var m = Math.floor((diff % 3600000) / 60000);
  if (d > 0) return t ? t('cardDurationDh', { d: d, h: h }) : (d + '일 ' + h + '시간');
  if (h > 0) return t ? t('cardDurationHm', { h: h, m: m }) : (h + '시간 ' + m + '분');
  return t ? t('cardDurationM', { m: m }) : (m + '분');
}

function AuctionBuyerAuctionDetail() {
  var navigate = useNavigate();
  var params = useParams();
  var auctionId = params.id;
  var t = useT();
  var loc = useLocale();
  var rateState = useExchangeRates();

  var [auction, setAuction] = useState(null);
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState('');
  var [bidModalOpen, setBidModalOpen] = useState(false);
  var [photoIdx, setPhotoIdx] = useState(0);
  // Wave UX-2.3 #19 — fullscreen viewer state
  var [viewerOpen, setViewerOpen] = useState(false);
  // Wave UX-2.4 #20 — 신고 모달
  var [reportOpen, setReportOpen] = useState(false);

  var load = useCallback(function() {
    setLoading(true);
    setError('');
    authBuyerFetch(API + '/buyer-app/auction/' + auctionId, {})
      .then(function(r) {
        if (isUnauthorized(r)) { handleUnauthorized(navigate); throw new Error('unauthorized'); }
        return r.json();
      })
      .then(function(d) {
        if (d && d.success && d.data && d.data.auction) {
          setAuction(d.data.auction);
        } else {
          setError(displayApiError(d, t, 'errAuctionNotFoundFallback'));
        }
        setLoading(false);
      })
      .catch(function(e) {
        if (e && e.message === 'unauthorized') return;
        setError(t('commonNetwork'));
        setLoading(false);
      });
  }, [auctionId, navigate, t]);

  useEffect(function() { load(); }, [load]);

  if (loading) {
    return (
      <AuctionBuyerLayout activeKey="auctions">
        <div style={{ textAlign: 'center', padding: 80, color: '#94a3b8' }}>{t('auctionsListLoading')}</div>
      </AuctionBuyerLayout>
    );
  }
  if (error || !auction) {
    return (
      <AuctionBuyerLayout activeKey="auctions">
        <div style={{ textAlign: 'center', padding: 80, color: '#dc2626' }}>
          {error || t('errAuctionNotFoundFallback')}
          <div style={{ marginTop: 16 }}>
            <button type="button" onClick={function(){navigate('/buyer/auctions');}}
              style={{ ...btnStyle, background: '#1B3A5C', color: 'white' }}>
              {t('detailBackToListShort')}
            </button>
          </div>
        </div>
      </AuctionBuyerLayout>
    );
  }

  var car = auction.car || {};
  var liveStats = auction.liveStats || {};
  var inspection = auction.inspection || {};
  var seller = auction.seller || {};

  var photoCount = (auction.photos && auction.photos.length) || 0;
  var photoPath = photoCount > 0 ? ('/buyer-app/auction/' + auction.id + '/photo/' + photoIdx) : null;

  // chore/dead-mask-fallback (2026-05-24) — mask.buildPublicAuctionResponse 정합 정리.
  // 백엔드 placeholder 항상 0 (myStatus_myAmount) 와 _my_bid_amount raw 컬럼 체인 제거.
  //   → mask 가 통합한 auction.myBid = { hasBid, amount, status, updateCount } 직접 사용.
  // mask 가 bid 없으면 myBid=null 반환 → 그대로 사용해도 안전.
  var existingBid = auction.myBid && auction.myBid.amount
    ? {
        amount: auction.myBid.amount,
        status: auction.myBid.status,
        updateCount: auction.myBid.updateCount || 0,
      }
    : null;
  var hasActiveBid = existingBid && existingBid.status === 'active';

  return (
    <AuctionBuyerLayout activeKey="auctions">
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 32px 100px' }}>
        {/* 뒤로 가기 */}
        <button type="button" onClick={function(){navigate('/buyer/auctions');}}
          style={{
            background: 'none', border: 'none', color: '#64748b',
            fontSize: 13, fontWeight: 600, cursor: 'pointer',
            padding: '8px 0', marginBottom: 16,
            display: 'flex', alignItems: 'center', gap: 4,          }}>
          {t('detailBackToList')}
        </button>

        <div className="buyer-detail-grid">
          {/* 좌측 — 사진 갤러리 */}
          <div>
            <div style={{ background: 'white', borderRadius: 12, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
              {photoPath ? (
                <div onClick={function(){ setViewerOpen(true); }} style={{ cursor: 'zoom-in' }}>
                  <AuthImage path={photoPath} alt=""
                    style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', display: 'block', background: '#f1f5f9' }}
                  />
                </div>
              ) : (
                <div style={{ width: '100%', aspectRatio: '4/3', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                  {t('cardNoPhoto')}
                </div>
              )}
              {photoCount > 1 && (
                <div style={{ display: 'flex', gap: 4, padding: 8, overflowX: 'auto', borderTop: '1px solid #f1f5f9' }}>
                  {Array.from({ length: photoCount }).map(function(_, i) {
                    return (
                      <button key={i} type="button" onClick={function(){setPhotoIdx(i);}}
                        style={{
                          flex: '0 0 80px', width: 80, height: 60,
                          padding: 0, border: photoIdx === i ? '2px solid #1B3A5C' : '1px solid #e2e8f0',
                          borderRadius: 6, overflow: 'hidden', cursor: 'pointer',
                          background: 'white',
                        }}>
                        <AuthImage path={'/buyer-app/auction/' + auction.id + '/photo/' + i}
                          alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* 차량 정보 */}
            <div style={{ background: 'white', borderRadius: 12, padding: 24, border: '1px solid #e2e8f0', marginTop: 16 }}>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', marginBottom: 8 }}>
                {car.year ? car.year + ' ' : ''}{car.model || auction.title || t('cardAuctionTitleFallback')}
              </h2>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13, color: '#64748b', marginBottom: 20 }}>
                {car.number && <span>{t('detailCarNumberLabel')}: <strong style={{ color: '#0f172a' }}>{car.number}</strong></span>}
                {car.mileage != null && <span>{t('detailMileageLabel')}: <strong style={{ color: '#0f172a' }}>{formatMileage(car.mileage)}</strong></span>}
                {car.fuelType && <span>{t('detailFuelLabel')}: <strong style={{ color: '#0f172a' }}>{car.fuelType}</strong></span>}
                {car.color && <span>{t('detailColorLabel')}: <strong style={{ color: '#0f172a' }}>{car.color}</strong></span>}
              </div>

              {/* 검차 요약 */}
              {inspection.summary && (
                <div style={{ background: '#f8fafc', borderRadius: 8, padding: 14, marginBottom: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 4 }}>{t('detailInspectionSummary')}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: inspection.summaryColor || '#0f172a' }}>{inspection.summary}</div>
                </div>
              )}

              {/* W6 — AUTODEX 시세 카드 (외부 바이어, KRW+USD) */}
              <MarketPriceCard model={car.model} year={car.year} mileage={car.mileage} locale={loc.locale} showUsd={true} />

              {/* 설명 */}
              {auction.description && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 6 }}>{t('detailDescription')}</div>
                  <div style={{ fontSize: 14, color: '#475569', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{auction.description}</div>
                </div>
              )}
            </div>
          </div>

          {/* 우측 — 가격/입찰 sticky 카드 */}
          <div style={{ position: 'sticky', top: 24 }}>
            {/* Wave UX-2.1 Item 5 — 시세 가이드 카드 (입찰 가격 결정 보조) */}
            <StatsCard model={car.model || auction.title || ''} />
            <div style={{ background: 'white', borderRadius: 12, padding: 24, border: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>{t('detailCurrentHighest')}</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#1B3A5C', marginTop: 4 }}>
              {auction.highestBid > 0 ? formatKrwAmountWithConvert(auction.highestBid, t, loc.locale, rateState.rates) : <span style={{ color: '#94a3b8', fontSize: 18 }}>{t('cardNoBid')}</span>}
            </div>

            <div style={{ display: 'flex', gap: 16, marginTop: 16, paddingTop: 16, borderTop: '1px solid #f1f5f9' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: '#94a3b8' }}>{t('detailBidCount')}</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a' }}>{liveStats.bidCount || 0}</div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: '#94a3b8' }}>{t('detailRemaining')}</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#dc2626' }}>{formatRemaining(auction.endsAt, t)}</div>
              </div>
            </div>

            {hasActiveBid && (
              <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: 12, marginTop: 16 }}>
                <div style={{ fontSize: 11, color: '#1e40af', fontWeight: 600 }}>{t('detailMyBid')}</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#1e40af', marginTop: 2 }}>
                  {formatKrwAmountWithConvert(existingBid.amount, t, loc.locale, rateState.rates)}
                </div>
                <div style={{ fontSize: 11, color: '#3b82f6', marginTop: 4 }}>
                  {t('detailMyBidEdits', { n: existingBid.updateCount })}
                </div>
              </div>
            )}

            <button type="button" onClick={function(){setBidModalOpen(true);}}
              disabled={existingBid && existingBid.updateCount >= 1}
              style={{
                width: '100%', height: 52,
                background: (existingBid && existingBid.updateCount >= 1)
                  ? '#cbd5e1'
                  : 'linear-gradient(135deg, #1B3A5C 0%, #3b82f6 100%)',
                color: 'white', border: 'none', borderRadius: 10,
                fontSize: 15, fontWeight: 700,
                cursor: (existingBid && existingBid.updateCount >= 1) ? 'not-allowed' : 'pointer',
                marginTop: 20,
                boxShadow: (existingBid && existingBid.updateCount >= 1) ? 'none' : '0 4px 12px rgba(27,58,92,0.2)',              }}>
              {(existingBid && existingBid.updateCount >= 1) ? t('detailUpdateLimitReached') : (hasActiveBid ? t('bidModalTitleEdit') : t('bidModalTitleNew'))}
            </button>

            {/* 출품자 정보 — Wave UX-2.4 #22 (2026-05-23) 신뢰 패널 확장 */}
            {seller.companyName && (
              <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid #f1f5f9' }}>
                <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, marginBottom: 6 }}>{t('detailSeller')}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{seller.companyName}</div>
                  {seller.trustLevel && (
                    <span style={trustChipStyle(seller.trustLevel)}>
                      {t('detailSellerTrust' + capitalize(seller.trustLevel))}
                    </span>
                  )}
                </div>
                {seller.trustSpeedGrade && (
                  <div style={{ marginTop: 6 }}>
                    <TrustSpeedBadge grade={seller.trustSpeedGrade} speed={seller.trustSpeed}
                      label={t(gradeI18nKey(seller.trustSpeedGrade))} size="md" />
                  </div>
                )}
                {(seller.totalSells != null || seller.listingsCount != null) && (
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                    {t('detailSellerStats', { sells: seller.totalSells || 0, listings: seller.listingsCount || 0 })}
                  </div>
                )}
                {seller.joinedAt && (function(){
                  var d = new Date(String(seller.joinedAt).replace(' ', 'T'));
                  if (isNaN(d.getTime())) return null;
                  return (
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                      {t('detailSellerJoinedYM', { year: d.getFullYear(), month: d.getMonth() + 1 })}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Wave UX-2.4 #20 — 신고 버튼 (sidebar 하단, 절제된 톤) */}
            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <button type="button" onClick={function(){ setReportOpen(true); }}
                style={{
                  background: 'none', border: 'none', padding: '4px 8px',
                  color: '#94a3b8', fontSize: 12, fontWeight: 600,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}>
                🚩 {t('reportButton')}
              </button>
            </div>
            </div>{/* end bid sticky inner */}
          </div>{/* end sticky wrapper */}
        </div>

        {/* 모바일 sticky 하단 버튼 — 작은 화면 전용 (선택, 후속) */}

        {bidModalOpen && (
          <AuctionBuyerBidModal auction={auction} existingBid={hasActiveBid ? existingBid : null}
            onClose={function(){setBidModalOpen(false);}}
            onSuccess={function(){ load(); }}
          />
        )}

        {/* Wave UX-2.3 #19 — 사진 fullscreen viewer */}
        {viewerOpen && photoCount > 0 && (
          <PhotoViewerModal auctionId={auction.id} count={photoCount} startIdx={photoIdx}
            onClose={function(){setViewerOpen(false);}}
          />
        )}

        {/* Wave UX-2.4 #20 — 신고 모달 */}
        {reportOpen && (
          <ReportModal auctionId={auction.id}
            onClose={function(){ setReportOpen(false); }}
          />
        )}
      </div>
    </AuctionBuyerLayout>
  );
}

var btnStyle = {
  padding: '10px 20px', borderRadius: 8, border: 'none',
  fontSize: 14, fontWeight: 700, cursor: 'pointer',};

// Wave UX-2.4 #22 (2026-05-23) — 신뢰 뱃지 색상 매핑 (5 단계)
function trustChipStyle(level) {
  var colors = {
    newbie:   { bg: '#f1f5f9', fg: '#64748b' },
    bronze:   { bg: '#fef3c7', fg: '#92400e' },
    silver:   { bg: '#e2e8f0', fg: '#475569' },
    gold:     { bg: '#fef9c3', fg: '#a16207' },
    platinum: { bg: '#dbeafe', fg: '#1e40af' },
  };
  var c = colors[level] || colors.newbie;
  return {
    background: c.bg, color: c.fg,
    fontSize: 11, fontWeight: 700,
    padding: '2px 8px', borderRadius: 999,
    lineHeight: 1.4,
  };
}

function capitalize(s) {
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export default AuctionBuyerAuctionDetail;
